import { useRef, useEffect } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { sessionRecord, liveAgentRecord } from '../platform/session/session';
import { createLiveAgent } from './liveAgent';
import { IDLE_TTL_MS } from './constants';

// What a journey without the block gets — all inert, and checkIdleExpired must not throw.
// See .claude/context/live-agent.md ("INERT_BLOCK").
const INERT_BLOCK = {
  initiateAgentChat: () => Promise.resolve(),
  sendAgentMessage: () => Promise.resolve(),
  sendAgentImage: () => Promise.resolve(),
  updateLastActivity: () => {},
  handleIdleDisconnect: () => Promise.resolve(),
  checkIdleExpired: () => false,
  openSurveyOverlay: () => {},
  closeSurveyOverlay: () => {},
  onSurveyDismiss: () => {},
};

/**
 * The only React file in LiveAgent. Membership ratchets: it can be granted late, never revoked.
 * See .claude/context/live-agent.md "Module shape" and "Membership is a ratchet".
 *
 * @param {Object}  options
 * @param {boolean} [options.enabled]   whether the active journey declared the block.
 * @param {Object}  options.store       the orchestrator store.
 * @param {Object}  options.conversation  the Conversation port.
 * @param {string}  [options.deviceType]  the surface the escalation comes from, sent on the
 *   Sprinklr conversation context (MAG4-856). Derived at the composition root from
 *   ExperienceContext so this block never reads it — the root owns the experience.
 * @returns {Object} the block's capability surface, or an inert stand-in when not declared.
 */
export const useLiveAgentBridge = ({
  store, conversation, enabled = true, deviceType,
}) => {
  // Temporary: prefer Experience Config sprinklr-idle-timeout when provided.
  // TODO: Remove this override and use constants-only idle TTL once rollout is complete.
  const sprinklrIdleTTLfromConfig = useConfigService('sprinklr-idle-timeout');
  const resolvedIdleTTL = Number(sprinklrIdleTTLfromConfig);
  const idleTTL = Number.isFinite(resolvedIdleTTL) && resolvedIdleTTL > 0
    ? resolvedIdleTTL
    : IDLE_TTL_MS;
  // End temporary idle-time override.

  // Ratcheted membership — see .claude/context/live-agent.md. Read this, never the live
  // `enabled` prop, so the block and the engine's own guards never disagree.
  const isMemberRef = useRef(enabled);
  if (enabled) isMemberRef.current = true;
  const isMember = isMemberRef.current;

  const contextRef = useRef(null);
  contextRef.current = {
    enabled: isMember,
    store,
    conversation,
    // Identity is read, never written, here. `get()` reads the persisted record, so this is
    // the id the Shell seeded rather than one this lazy chunk resolved for itself.
    sessionId: sessionRecord.get()?.sessionId,
    idleTTL,
    deviceType,
  };

  const blockRef = useRef(null);
  if (isMember && !blockRef.current) {
    blockRef.current = createLiveAgent({
      getContext: () => contextRef.current,
      // The `maa-live-agent` record — already the synchronous authority, so no ref-sync.
      // See .claude/context/live-agent.md ("Bridge persistence write path").
      persistence: {
        read: () => liveAgentRecord.get(),
        write: (next) => liveAgentRecord.set(next),
      },
    });
  }

  // ⚠ Depends on `isMember`, NOT `[]` — a late grant needs this to run init().
  // See .claude/context/live-agent.md ("Membership is a ratchet").
  useEffect(() => {
    const block = blockRef.current;
    if (!block) return undefined;
    block.init();
    return () => block.dispose();
  }, [isMember]);

  return blockRef.current ?? INERT_BLOCK;
};
